/**
 * Persists a request's source PDF in Cache Storage so it is downloaded once
 * and served from disk on every later visit — until the signed link expires
 * or the file changes (length differs).
 *
 * Why not the plain HTTP cache: the signed URL carries a SAS token in its
 * query string and may be re-signed per /pdf-url call (a different URL is a
 * guaranteed miss), and a cached full 200 is only sometimes reused for later
 * Range requests. Keying by request id makes reuse deterministic.
 *
 * Everything here is best-effort: any failure (no Cache Storage, quota,
 * storage disabled) degrades to today's network behaviour.
 */

const CACHE_NAME = 'docsila-source-pdf-v1';
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;
const LENGTH_HEADER = 'X-Docsila-Length';
const EXPIRES_HEADER = 'X-Docsila-Expires';

export interface CachedSourcePdf {
  blob: Blob;
  length: number;
  expiresAt: number;
}

function isCacheAvailable(): boolean {
  return typeof window !== 'undefined' && typeof caches !== 'undefined';
}

function keyFor(requestId: string): string {
  return `/__docsila-source-pdf__/${encodeURIComponent(requestId)}`;
}

async function openCache(): Promise<Cache | null> {
  try {
    return await caches.open(CACHE_NAME);
  } catch {
    return null;
  }
}

export const sourcePdfCache = {
  isAvailable(): boolean {
    return isCacheAvailable();
  },

  /** Link expiry: the Azure SAS `se=` (ISO) if present in the signed URL, else now + 24h. */
  expiryFromSignedUrl(url: string): number {
    const fallback = Date.now() + DEFAULT_TTL_MS;
    try {
      const se = new URL(url).searchParams.get('se');
      if (!se) {
        return fallback;
      }
      const parsed = Date.parse(se);
      return Number.isFinite(parsed) && parsed > Date.now() ? parsed : fallback;
    } catch {
      return fallback;
    }
  },

  /** Returns the stored PDF for this request, or null if absent, expired, or a different file. */
  async get(requestId: string, expectedLength: number): Promise<CachedSourcePdf | null> {
    if (!isCacheAvailable()) {
      return null;
    }
    const cache = await openCache();
    if (!cache) {
      return null;
    }
    const key = keyFor(requestId);
    try {
      const response = await cache.match(key);
      if (!response) {
        return null;
      }
      const length = Number(response.headers.get(LENGTH_HEADER));
      const expiresAt = Number(response.headers.get(EXPIRES_HEADER));
      const stale =
        !Number.isFinite(length) || length !== expectedLength
        || !Number.isFinite(expiresAt) || Date.now() >= expiresAt;
      if (stale) {
        await cache.delete(key);
        return null;
      }
      const blob = await response.blob();
      if (blob.size !== expectedLength) {
        await cache.delete(key);
        return null;
      }
      return { blob, length, expiresAt };
    } catch (err) {
      return null;
    }
  },

  /**
   * Stores a streamed body under our own metadata headers. The stream goes
   * straight to disk — nothing is buffered in JS. Resolves true on success.
   */
  async put(requestId: string, body: ReadableStream<Uint8Array>, length: number, expiresAt: number): Promise<boolean> {
    if (!isCacheAvailable()) {
      return false;
    }
    const cache = await openCache();
    if (!cache) {
      return false;
    }
    try {
      const response = new Response(body, {
        status: 200,
        headers: {
          'Content-Type': 'application/pdf',
          [LENGTH_HEADER]: String(length),
          [EXPIRES_HEADER]: String(expiresAt),
        },
      });
      await cache.put(keyFor(requestId), response);
      return true;
    } catch (err) {
      return false;
    }
  },

  async delete(requestId: string): Promise<void> {
    if (!isCacheAvailable()) {
      return;
    }
    const cache = await openCache();
    if (!cache) {
      return;
    }
    try {
      await cache.delete(keyFor(requestId));
    } catch {
      /* best-effort */
    }
  },
};
